import { useState } from "react";
import { toast } from "sonner";
import { fechaCorta } from "@/reclutamiento/logica";
import type { EstadoVacante, VacantePortal } from "@/reclutamiento/tipos";
import { CajaError, Esqueleto, Hoja } from "../componentes";
import { AVISO_SOLO_LECTURA, usePortal } from "../contexto";
import { Mk } from "../iconos";
import { claveCache, mensajeError, useCarga, type Semaforo } from "../util";

const ESTADO: Record<EstadoVacante, [string, string]> = {
  activa: ["ok", "Activa"],
  pausada: ["yel", "Pausada"],
  cerrada: ["neu", "Cerrada"],
  borrador: ["yel", "Falta tu firma"],
};

export function Vacantes() {
  const { api, sesion, soloLectura } = usePortal();
  const carga = useCarga(() => api.vacantes(), [api], undefined, claveCache(sesion, api.verComo, "vacantes"));
  const [cambio, setCambio] = useState<VacantePortal | null>(null);

  return (
    <>
      <div className="phello">
        <h1>Mis vacantes</h1>
        <p>Los criterios que firmaste. Si algo cambió, pídenos el ajuste.</p>
      </div>
      {carga.cargando && !carga.datos ? (
        <Esqueleto filas={2} alto={300} />
      ) : carga.error ? (
        <CajaError texto={carga.error} onReintentar={carga.recargar} />
      ) : !carga.datos?.length ? (
        <p className="empty">Aún no tienes vacantes.</p>
      ) : (
        carga.datos.map((v) => <TarjetaVacante key={v.id} v={v} onCambio={() => setCambio(v)} soloLectura={soloLectura} />)
      )}
      {cambio && <HojaCambio v={cambio} onCerrar={() => setCambio(null)} />}
    </>
  );
}

function TarjetaVacante({ v, onCambio, soloLectura }: { v: VacantePortal; onCambio: () => void; soloLectura: boolean }) {
  const [clase, etiqueta] = ESTADO[v.estado];
  const des = v.criterios.filter((c) => c.efecto === "descarta").map((c) => c.texto_no || c.texto);
  const mar = v.criterios
    .filter((c) => c.efecto === "marca")
    .map((c) => c.texto_no || c.texto)
    .concat(["Cualquier dato por confirmar: CV sin fechas o respuesta incompleta"]);
  const cumpleExplicitos = v.criterios.filter((c) => c.efecto === "cumple").map((c) => c.texto);
  // Si no hay criterios de «cumple», lo que cuenta como cumple es el lado bueno de los demás (como la maqueta).
  const cum = cumpleExplicitos.length ? cumpleExplicitos : v.criterios.map((c) => c.texto);

  return (
    <article className="vcard" aria-labelledby={`va-${v.id}`}>
      <div className="vh">
        <div>
          <h2 id={`va-${v.id}`}>{v.puesto}</h2>
          <p>{v.zona}</p>
        </div>
        <span className={`pill ${clase}`}>
          {clase !== "neu" && <Mk k={clase as Semaforo} s={14} />}
          {etiqueta}
        </span>
      </div>
      <dl className="kv" style={{ fontSize: 14 }}>
        {v.horario && (
          <>
            <dt>Horario</dt>
            <dd>{v.horario}</dd>
          </>
        )}
        {v.sueldo && (
          <>
            <dt>Sueldo</dt>
            <dd>{v.sueldo}</dd>
          </>
        )}
        <dt>Firmada</dt>
        <dd className="num">{v.firmada_en ? fechaCorta(v.firmada_en) : "Sin firmar"}</dd>
      </dl>
      <div className="lists3">
        <Lista k="red" titulo="Descarta si…" items={des.length ? des : ["Nada descarta: todo marca para revisar"]} />
        <Lista k="yel" titulo="Marca para revisar si…" items={mar} />
        <Lista k="ok" titulo="Cuenta como cumple si…" items={cum.length ? cum : ["Sin criterios todavía"]} />
      </div>
      <button
        className="btn"
        style={{ alignSelf: "flex-start" }}
        disabled={soloLectura}
        title={soloLectura ? AVISO_SOLO_LECTURA : undefined}
        onClick={onCambio}
      >
        Pedir un cambio
      </button>
    </article>
  );
}

function Lista({ k, titulo, items }: { k: Semaforo; titulo: string; items: string[] }) {
  return (
    <div className="l3">
      <h3>
        <Mk k={k} s={15} />
        {titulo}
      </h3>
      <ul>
        {items.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

function HojaCambio({ v, onCerrar }: { v: VacantePortal; onCerrar: () => void }) {
  const { api, soloLectura } = usePortal();
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    if (soloLectura) return void toast.error(AVISO_SOLO_LECTURA);
    if (!texto.trim()) return void toast.error("Escribe qué quieres cambiar.");
    setEnviando(true);
    try {
      await api.pedirCambio(v.id, texto);
      toast.success("Listo, le llegó a la agencia");
      onCerrar();
    } catch (e) {
      toast.error(mensajeError(e, "No se pudo enviar. Intenta de nuevo."));
      setEnviando(false);
    }
  };
  return (
    <Hoja titulo="Pedir un cambio" onCerrar={onCerrar}>
      <p className="muted" style={{ fontSize: 14 }}>
        {v.puesto}. Nos llega a nosotros y te contestamos antes de mover el filtro.
      </p>
      <label className="field">
        ¿Qué quieres cambiar?
        <textarea
          className="inp"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Ej.: que ya no descarte si no tiene escáner, solo que lo marque"
        />
      </label>
      <div className="row">
        <span className="spacer" />
        <button className="btn ghost" onClick={onCerrar}>
          Cancelar
        </button>
        <button className="btn primary" onClick={enviar} disabled={enviando || !texto.trim()}>
          {enviando ? "Enviando…" : "Enviar"}
        </button>
      </div>
    </Hoja>
  );
}
